import React, { useState, useMemo, useCallback } from "react";
import Breadcrumbs from "../components/Breadcrumbs";

import { format } from "date-fns";
import { 
  Plus, 
  Eye, 
  CheckCircle, 
  XCircle, 
  FileText, 
  Edit2, 
  Clock, 
  ArrowLeft,
  Search,
  Filter
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useBlogPosts } from "../hooks/useBlogPosts";
import { DashboardLayout } from "../layouts/DashboardLayout";

// Types
type SettingsTab = 'published' | 'pending_review' | 'rejected';

interface Post {
  id: string;
  title: string;
  content?: string;
  status: string;
  created_at: number;
  date_created?: number;
}

interface ConfirmationState {
  id: string;
  action: "published" | "rejected";
  reason?: string;
}

// Components
const LoadingSpinner: React.FC = () => (
  <motion.div
    className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
  </motion.div>
);

const ConfirmationModal: React.FC<{
  confirmation: ConfirmationState;
  onConfirm: () => void;
  onCancel: () => void;
  onReasonChange: (reason: string) => void;
}> = ({ confirmation, onConfirm, onCancel, onReasonChange }) => (
  <motion.div
    className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-20"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <motion.div
      className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4"
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
    >
      <h3 className="text-lg font-semibold mb-4">Confirm Action</h3>
      <p className="text-gray-600 mb-4">
        Are you sure you want to {confirmation.action === "published" ? "publish" : "reject"} this post?
      </p>
      
      {confirmation.action === "rejected" && (
        <div className="mb-4">
          <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700 mb-2">
            Reason for rejection:
          </label>
          <textarea
            id="rejectionReason"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            value={confirmation.reason || ''}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="Please provide a reason for rejection..."
          />
        </div>
      )}
      
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className={`px-4 py-2 text-white rounded-md transition-colors ${
            confirmation.action === "published"
              ? "bg-green-600 hover:bg-green-700"
              : "bg-red-600 hover:bg-red-700"
          }`}
        >
          {confirmation.action === "published" ? "Publish" : "Reject"}
        </button>
      </div>
    </motion.div>
  </motion.div>
);

const PostModal: React.FC<{
  post: Post;
  onClose: () => void;
}> = ({ post, onClose }) => (
  <motion.div
    className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-20"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <motion.div
      className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden"
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
    >
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{post.title || "Untitled Post"}</h2>
            {(post.date_created || post.created_at) && (
              <div className="flex items-center text-sm text-gray-500 mt-2">
                <Clock className="h-4 w-4 mr-1" />
                Submitted on {format(
                  new Date((post.date_created || post.created_at) * 1000),
                  "MMMM d, yyyy 'at' h:mm a"
                )}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>
      </div>
      
      <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
        <div className="prose max-w-none">
          {post.content ? (
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          ) : (
            <p className="text-gray-500 italic">No content available.</p>
          )}
        </div>
      </div>
    </motion.div>
  </motion.div>
);

const PostCard: React.FC<{
  post: Post;
  onView: (id: string) => void;
  onAction?: (id: string, action: "published" | "rejected") => void;
  showActions?: boolean;
}> = ({ post, onView, onAction, showActions = false }) => (
  <motion.div
    className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.2 }}
  >
    <div className="flex-1">
      <h3 className="text-lg font-medium text-gray-900 mb-1">
        {post.title || "Untitled Post"}
      </h3>
      <div className="flex items-center text-sm text-gray-500">
        <Clock className="h-4 w-4 mr-1" />
        {post.status === "published" ? "Published" : "Submitted"} on{" "}
        {format(
          new Date((post.date_created || post.created_at) * 1000),
          "MMMM d, yyyy"
        )}
      </div>
    </div>
    
    <div className="flex items-center gap-2">
      <button
        onClick={() => onView(post.id)}
        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
        title="View post"
      >
        <Eye className="h-5 w-5" />
      </button>
      
      {showActions && onAction && (
        <>
          <button
            onClick={() => onAction(post.id, "published")}
            className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md transition-colors"
            title="Approve post"
          >
            <CheckCircle className="h-5 w-5" />
          </button>
          <button
            onClick={() => onAction(post.id, "rejected")}
            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
            title="Reject post"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </>
      )}
    </div>
  </motion.div>
);

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="text-center py-12">
    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
    <p className="text-gray-500">{message}</p>
  </div>
);

// Main Component
export const AdminBlogPage: React.FC = () => {
  const { posts, loading, error, updatePostStatus } = useBlogPosts();
  const [review, setReview] = useState('This blog has been approved');
  const [isLoading, setIsLoading] = useState(false);
  const [confirmation, setConfirmation] = useState<ConfirmationState | null>(null);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<SettingsTab>("pending_review");
  const [searchTerm, setSearchTerm] = useState("");

  // Memoized filtered posts
  const filteredPosts = useMemo(() => {
    let filtered = posts.filter((post) => post.status === activeTab);
    
    if (searchTerm) {
      filtered = filtered.filter((post) =>
        post.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered.sort((a, b) => 
      (b.date_created || b.created_at) - (a.date_created || a.created_at)
    );
  }, [posts, activeTab, searchTerm]);

  const selectedPost = useMemo(() => 
    posts.find(post => post.id === selectedPostId),
    [posts, selectedPostId]
  );

  // Handlers
  const handleAction = useCallback((id: string, action: "published" | "rejected") => {
    setConfirmation({ id, action });
  }, []);

  const confirmAction = useCallback(async () => {
    if (!confirmation) return;
    
    setIsLoading(true);
    try {
      await updatePostStatus(confirmation.id, {
        status: confirmation.action,
        review_results: review,
        rejection_reason: confirmation.reason
      });
    } catch (error) {
      console.error('Failed to update post status:', error);
    } finally {
      setIsLoading(false);
      setConfirmation(null);
    }
  }, [confirmation, review, updatePostStatus]);

  const cancelAction = useCallback(() => {
    setConfirmation(null);
  }, []);

  const handleReasonChange = useCallback((reason: string) => {
    setConfirmation(prev => prev ? { ...prev, reason } : null);
  }, []);

  const handleViewPost = useCallback((id: string) => {
    setSelectedPostId(id);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedPostId(null);
  }, []);

  // Tab configuration
  const tabs: { id: SettingsTab; label: string; icon: React.ReactNode; count: number }[] = [
    {
      id: 'pending_review',
      label: 'Pending Review',
      icon: <Clock className="text-yellow-500" />,
      count: posts.filter(p => p.status === 'pending_review').length
    },
    {
      id: 'published',
      label: 'Published',
      icon: <FileText className="text-green-500" />,
      count: posts.filter(p => p.status === 'published').length
    },
    {
      id: 'rejected',
      label: 'Rejected',
      icon: <XCircle className="text-red-500" />,
      count: posts.filter(p => p.status === 'rejected').length
    }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading posts...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-red-600">
            <XCircle className="h-12 w-12 mx-auto mb-4" />
            <p className="text-lg font-semibold">Error loading posts</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <motion.div
        className="flex-1 p-4 md:p-8 relative"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <AnimatePresence>
          {isLoading && <LoadingSpinner />}
          {confirmation && (
            <ConfirmationModal
              confirmation={confirmation}
              onConfirm={confirmAction}
              onCancel={cancelAction}
              onReasonChange={handleReasonChange}
            />
          )}
          {selectedPostId && selectedPost && (
            <PostModal
              post={selectedPost}
              onClose={handleCloseModal}
            />
          )}
        </AnimatePresence>

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Blog Management</h1>
              <p className="text-gray-600 mt-1">Review and manage blog posts</p>
            </div>
            
            {/* Search */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent w-full"
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm border">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex-1 py-4 px-6 text-sm font-medium text-center transition-colors hover:bg-gray-50 focus:z-10 ${
                    activeTab === tab.id
                      ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                      : 'text-gray-500 border-b-2 border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-center space-x-2">
                    {tab.icon}
                    <span>{tab.label}</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      activeTab === tab.id
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {tab.count}
                    </span>
                  </div>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="min-h-[400px]">
            {filteredPosts.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {filteredPosts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onView={handleViewPost}
                    onAction={activeTab === 'pending_review' ? handleAction : undefined}
                    showActions={activeTab === 'pending_review'}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                message={
                  searchTerm
                    ? `No posts found matching "${searchTerm}"`
                    : `No ${activeTab.replace('_', ' ')} posts found`
                }
              />
            )}
          </div>
        </div>
      </motion.div>
    </DashboardLayout>
  );
};